import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchTCSTransactionBreakdown } from '../../store/tcsSettlementStore/tcsSettlementThunk';
import { ApiStatus } from '../../utils/api.types';

interface TCSTransactionSummaryModalProps {
  open: boolean;
  onClose: () => void;
  fromDate: string;
  toDate: string;
}

const TCSTransactionSummaryModal: React.FC<TCSTransactionSummaryModalProps> = ({
  open,
  onClose,
  fromDate,
  toDate
}) => {
  const dispatch = useAppDispatch();
  const { transactionBreakdown, breakdownStatus } = useAppSelector(
    (state) => state.tcsSettlement
  );

  const [activeTab, setActiveTab] = React.useState(0);

  useEffect(() => {
    if (open && fromDate && toDate) {
      dispatch(fetchTCSTransactionBreakdown({ fromDate, toDate }));
    }
  }, [open, fromDate, toDate, dispatch]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  };

  const exportToExcel = () => {
    // TODO: Implement Excel export functionality
    console.log('Export to Excel');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '900px'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" fontWeight="600">
              TCS Transaction Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Period: {format(new Date(fromDate), 'dd MMM yyyy')} - {format(new Date(toDate), 'dd MMM yyyy')}
            </Typography>
          </Box>
          <Box>
            {/* <Tooltip title="Export to Excel">
              <IconButton onClick={exportToExcel} size="small" sx={{ mr: 1 }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip> */}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {breakdownStatus === ApiStatus.LOADING ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <CircularProgress />
          </Box>
        ) : transactionBreakdown ? (
          <>
            {/* Summary Cards */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Box sx={{ p: 2, flex: 1, border: '1px solid #f44336', borderRadius: 5, position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <TrendingDownIcon color="error" sx={{ fontSize: 32, opacity: 0.7 }} />
                </Box>
                <Typography variant="subtitle2"  fontWeight="600" mb={1}>
                  TCS Payable
                </Typography>
                <Typography variant="h4" fontWeight="700" color="error.main">
                  {formatCurrency(transactionBreakdown.summary.totalTCSPayable)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {transactionBreakdown.summary.payableCount} invoices
                </Typography>
              </Box>

              <Box sx={{ p: 2, flex: 1, border: '1px solid #4caf50', borderRadius: 5, position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <TrendingUpIcon color="success" sx={{ fontSize: 32, opacity: 0.7 }} />
                </Box>
                <Typography variant="subtitle2" fontWeight="600" mb={1}>
                  TCS Receivable
                </Typography>
                <Typography variant="h4" fontWeight="700" color="success.main">
                  {formatCurrency(transactionBreakdown.summary.totalTCSReceivable)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {transactionBreakdown.summary.receivableCount} invoices
                </Typography>
              </Box>

              <Box color="primary.main" sx={{ p: 2, flex: 1, border: '1px solid ', borderRadius: 5, position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <TrendingUpIcon color="primary" sx={{ fontSize: 32, opacity: 0.7 }} />
                </Box>
                <Typography color="black" variant="subtitle2" fontWeight="600" mb={1}>
                  Net TCS
                </Typography>
                <Typography variant="h4" fontWeight="700" color="primary.main">
                  {formatCurrency(Math.abs(transactionBreakdown.summary.netTCS))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {transactionBreakdown.summary.netTCS >= 0 ? 'Payable' : 'Receivable'}
                </Typography>
              </Box>
            </Box>

            {/* Tabs for Payable and Receivable */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>TCS Payable</Typography>
                      <Chip
                        label={transactionBreakdown.summary.payableCount}
                        size="small"
                        color="error"
                      />
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>TCS Receivable</Typography>
                      <Chip
                        label={transactionBreakdown.summary.receivableCount}
                        size="small"
                        color="success"
                      />
                    </Box>
                  }
                />
              </Tabs>
            </Box>

            {/* TCS Payable Table */}
            {activeTab === 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {transactionBreakdown.payable.description}
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Ledger: {transactionBreakdown.payable.ledger}
                </Typography>
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Txn ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Invoice</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Principal</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Tax</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">TCS Amount</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>TCS Section</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactionBreakdown.payable.transactions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">No TCS payable transactions found</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        transactionBreakdown.payable.transactions.map((txn) => (
                          <TableRow key={txn._id} hover>
                            <TableCell>{format(new Date(txn.date), 'dd MMM yyyy')}</TableCell>
                            <TableCell>
                              <Chip label={txn.transactionId} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>{txn.invoiceNumber}</TableCell>
                            <TableCell>{txn.counterParty}</TableCell>
                            <TableCell>
                              <Chip
                                label={txn.type === 'SALES_INVOICE' ? 'Sales' : 'Purchase'}
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">{formatCurrency(txn.principalAmount)}</TableCell>
                            <TableCell align="right">{formatCurrency(txn.taxAmount)}</TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="600" color="error.main">
                                {formatCurrency(txn.tcsAmount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={txn.tcsSection} size="small" color="primary" variant="outlined" />
                            </TableCell>
                            <TableCell align="right">{formatCurrency(txn.totalAmount)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* TCS Receivable Table */}
            {activeTab === 1 && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {transactionBreakdown.receivable.description}
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Ledger: {transactionBreakdown.receivable.ledger}
                </Typography>
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Txn ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Invoice</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Principal</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Tax</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">TCS Amount</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>TCS Section</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactionBreakdown.receivable.transactions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">No TCS receivable transactions found</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        transactionBreakdown.receivable.transactions.map((txn) => (
                          <TableRow key={txn._id} hover>
                            <TableCell>{format(new Date(txn.date), 'dd MMM yyyy')}</TableCell>
                            <TableCell>
                              <Chip label={txn.transactionId} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>{txn.invoiceNumber}</TableCell>
                            <TableCell>{txn.counterParty}</TableCell>
                            <TableCell>
                              <Chip
                                label={txn.type === 'SALES_INVOICE' ? 'Sales' : 'Purchase'}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">{formatCurrency(txn.principalAmount)}</TableCell>
                            <TableCell align="right">{formatCurrency(txn.taxAmount)}</TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="600" color="success.main">
                                {formatCurrency(txn.tcsAmount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={txn.tcsSection} size="small" color="primary" variant="outlined" />
                            </TableCell>
                            <TableCell align="right">{formatCurrency(txn.totalAmount)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <Typography color="text.secondary">No data available</Typography>
          </Box>
        )}
      </DialogContent>
      {/* <Divider />
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions> */}
    </Dialog>
  );
};

export default TCSTransactionSummaryModal;
